// Pixel X App integration helper
import * as Sentry from '@sentry/react';

interface PixelXAppParams {
  event_name: string;
  lead_name?: string;
  lead_email?: string;
  lead_phone?: string;
}

/**
 * Formata o número de telefone para o padrão internacional (55XXXXXXXXXXX)
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove tudo que não é número
  const numbers = phone.replace(/\D/g, '');
  
  Sentry.addBreadcrumb({
    category: 'validation',
    message: 'Phone number formatted',
    data: {
      originalLength: phone.length,
      numbersLength: numbers.length,
      hasCountryCode: numbers.startsWith('55'),
    },
  });
  
  // Adiciona código do país 55 se não tiver
  if (numbers.length === 11 && !numbers.startsWith('55')) {
    return '55' + numbers;
  }
  
  return numbers;
};

/**
 * Envia evento para o Pixel X App
 */
export const sendPixelEvent = async (
  eventName: 'AddToCart' | 'Lead',
  data?: {
    lead_name?: string;
    lead_email?: string;
    lead_phone?: string;
  }
): Promise<void> => {
  const startTime = Date.now();
  
  // Breadcrumb: tentativa de enviar evento
  Sentry.addBreadcrumb({
    category: 'pixel',
    message: `Attempting to send ${eventName} event`,
    level: 'info',
    data: {
      eventName,
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : [],
    },
  });
  
  try {
    // Verificações com logging
    if (typeof window === 'undefined') {
      throw new Error('Window is undefined');
    }

    if (!window.pixel_x_app) {
      Sentry.captureMessage('Pixel X App not loaded', {
        level: 'warning',
        tags: { pixel_issue: 'not_loaded' },
      });
      throw new Error('Pixel X App not loaded');
    }

    if (typeof window.pixel_x_app.send_event !== 'function') {
      Sentry.captureMessage('Pixel X App send_event is not a function', {
        level: 'error',
        tags: { pixel_issue: 'invalid_api' },
      });
      throw new Error('Pixel X App send_event is not a function');
    }

    const params: PixelXAppParams = {
      event_name: eventName,
      ...data,
    };

    // Timeout de 5 segundos
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Pixel X App timeout after 5000ms')), 5000);
    });

    await Promise.race([
      window.pixel_x_app.send_event(params),
      timeoutPromise
    ]);

    const duration = Date.now() - startTime;
    
    // Breadcrumb: sucesso
    Sentry.addBreadcrumb({
      category: 'pixel',
      message: `${eventName} event sent successfully`,
      level: 'info',
      data: {
        duration: `${duration}ms`,
        eventName,
      },
    });

    // Log de performance se demorar muito
    if (duration > 3000) {
      Sentry.captureMessage(`Pixel X App slow response: ${duration}ms`, {
        level: 'warning',
        tags: { 
          pixel_issue: 'slow_response',
          event_name: eventName 
        },
      });
    }

    // Log apenas em desenvolvimento
    if (import.meta.env.DEV) {
      console.log('Pixel X App event sent:', params);
    }

  } catch (error) {
    const duration = Date.now() - startTime;
    
    // Capturar erro com contexto completo
    Sentry.captureException(error, {
      tags: {
        pixel_event: eventName,
        pixel_failure: 'true',
      },
      contexts: {
        pixel: {
          eventName,
          duration: `${duration}ms`,
          data,
          pixelAvailable: typeof window !== 'undefined' && !!window.pixel_x_app,
          sendEventAvailable: typeof window !== 'undefined' && !!window.pixel_x_app?.send_event,
        },
      },
    });
    
    // Não quebra o fluxo do usuário
    console.error('Error sending Pixel X App event:', error);
  }
};
